import { Validator } from '@cfworker/json-schema';
import { formatEntityName, formatFieldName, formatPluginName, parseParams, printStmtArguments, } from '../../../common';
import { createSchemaStatementHandler } from '../../createSchemaStatementHandler';
import { SchemaDslBuilderError } from '../../SchemaDslBuilder';
export const addPluginStmtInstruction = `
#### Apply plugin to entity field
- Syntax: \`add plugin <plugin_type> <entity_name>[.<field_name>][ with <key> = "<value>"[, <key2> = "<value2>"]];\`
- Example: \`add plugin person User.person;\`
- Example: \`add plugin richText Article.content;\`
- Example: \`add plugin image Profile.avatar;\`
- Example: \`add plugin board Project with columns = "ProjectStatus", columnsSortableBy="order", discriminationField = "status", maxTasks = 100, visible = true\`
- Example: \`add plugin board Project.url with derivedFrom = "name"\`
`;
export const stmtAddPlugin = ({ pluginName, entityName, fieldName, params }) => {
    return `add plugin ${pluginName} ${entityName}${fieldName ? `.${fieldName}` : ''}${params ? ` with ${printStmtArguments(params)}` : ''};`;
};
export const stmtForHumanAddPlugin = ({ pluginName, entityName, fieldName, params }) => {
    return `Apply the plugin __${pluginName}__ to the ${fieldName ? `column __${fieldName}__ in` : ''} table __${entityName}__ with the following parameters: ${printStmtArguments(params)}.`;
};
export const createAddPluginHandler = (plugins) => createSchemaStatementHandler({
    type: 'add plugin',
    parse: line => {
        const match = /^(add|create) (plugin|blueprint) (?<pluginName>[\w-]+) (?<entityName>\w+)(?:\.(?<fieldName>\w+))?(?: with (?<params>[^;]+))?/.exec(line);
        return match?.groups
            ? {
                type: 'add plugin',
                entityName: formatEntityName(match.groups.entityName),
                pluginName: formatPluginName(match.groups.pluginName),
                fieldName: match.groups.fieldName ? formatFieldName(match.groups.fieldName) : undefined,
                params: parseParams(match.groups.params),
            }
            : null;
    },
    getHumanStmt: stmtForHumanAddPlugin,
    apply: (stmt, builder) => {
        const plugin = plugins[stmt.pluginName];
        if (!plugin) {
            throw new SchemaDslBuilderError(`Unknown plugin name ${stmt.pluginName}. Available plugins: ${Object.keys(plugins).join(', ')}`);
        }
        const pluginArgs = plugin.handlerArgs;
        if (pluginArgs && 'required' in pluginArgs && Array.isArray(pluginArgs.required) && pluginArgs.required.length && !stmt.params) {
            throw new SchemaDslBuilderError(`Plugin ${stmt.pluginName} requires arguments. Required arguments: ${pluginArgs.required.join(', ')}.`);
        }
        if (pluginArgs && 'type' in pluginArgs) {
            const validator = new Validator(pluginArgs);
            const validatorResult = validator.validate(stmt.params);
            if (!validatorResult.valid) {
                const validatorError = validatorResult.errors.map(error => error.error).join(', ');
                throw new SchemaDslBuilderError(`Invalid plugin arguments for plugin ${stmt.pluginName}: ${validatorError}`);
            }
        }
        builder.updateSchema(it => {
            if (stmt.fieldName) {
                const entity = it.schema.model.entities[stmt.entityName];
                const field = entity.fields[stmt.fieldName];
                if (field) {
                    it = it.removeField(stmt.entityName, stmt.fieldName);
                }
            }
            return plugin.processPlugin({ stmt, schemaBuilder: it });
        });
        builder.updateSchemaExtensions(it => {
            const newPlugin = { name: stmt.pluginName, type: plugin.type, schema: plugin.schema, handlerArgs: pluginArgs };
            const existingFields = it.plugins[stmt.pluginName]?.connectedTo?.[stmt.entityName]?.fields || [];
            it.plugins[stmt.pluginName] = {
                ...newPlugin,
                ...it.plugins[stmt.pluginName],
                connectedTo: {
                    ...it.plugins[stmt.pluginName]?.connectedTo,
                    [stmt.entityName]: {
                        ...it.plugins[stmt.pluginName]?.connectedTo?.[stmt.entityName],
                        ...(stmt.fieldName ? { fields: [...existingFields, stmt.fieldName] } : {}),
                        ...(stmt.params ? { params: stmt.params } : {}),
                    },
                },
            };
        });
    },
});
//# sourceMappingURL=AddPluginStmt.js.map